import {BaseElement, BaseElementAsync, MsgLabel, OSkinableInit} from "back/commons/basis";
import {WSP, Wsp} from "lib/wsp/wsp";
import {JSrcFields, SRC} from "lib/wsp/src";
import {DOMSH} from "lib/commons/xml/domsh";
import {DOM, JSX} from "lib/commons/xml/dom";
import {REG} from "lib/commons/registry";
import {CDM} from "lib/commons/utils/cdm";
import {REMOTE, RemoteRes} from "lib/core/remote";
import {Button, OButtonInit} from "back/commons/widgets/buttons";

/** Widget RemoteView sans la nouvelle approche JRemoteResDesc à partir sc6.1 -  TODOsc7 à suppr ?*/
export interface RemoteView extends BaseElement {
	initialize(init: ORemoteViewInit): this
}

export interface ORemoteViewInit extends OSkinableInit, ORemoteviewer {
	tag?: 'img' | 'video' | 'audio' | 'iframe'
}

/**
 * Le premier viewer dont la requête HEAD est OK est utilisé
 * Exception : ce test validité n'est pas exécuté sur "locationAsSrc" SANS queryStringParams
 * qui doit donc logiquement être défini en dernière position (besoin authentificion)
 */
interface ORemoteviewer {
	viewers: (
		{ type: 'cidPreviewProcess', is: string, metasIn?: { string: { value: string, is?: string } } } /* TODO cidPreviewProcess */
		| { type: 'scDepotView', code: string }
		| { type: 'locationAsSrc', queryStringParams?: { keySvc?: 'scDepot' | string, value: any }[] }
		)[]
}

export class RemoteView extends BaseElement {

	protected config: ORemoteViewInit;

	protected _wsp: Wsp;

	protected _fields: JSrcFields;

	protected _tryIndex: number;

	protected _fixedLocation: string;

	protected _mainDisplayTag: HTMLIFrameElement | HTMLImageElement | HTMLAudioElement | HTMLVideoElement;
	protected _pending: MsgLabel;

	get wsp(): Wsp {return this._wsp || this.findReg(this.config).env.wsp}

	set wsp(v: Wsp) {
		this._wsp = v;
		this._fields = null;
		this.refresh();
	}

	get fields(): JSrcFields {return this._fields || this.findReg(this.config).env.longDesc}

	set fields(v: JSrcFields) {
		this._fields = v;
		this.refresh();
	}

	get location(): string {return this._fixedLocation}

	set location(v: string) {
		this._fixedLocation = v;
		this.refresh();
	}

	protected _initialize(init: ORemoteViewInit) {
		this.config = init;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		switch (this.config.tag) {
		case 'img':
			this._mainDisplayTag = sr.appendChild(<img class="imgView" hidden=""/>) as HTMLImageElement;
			break;
		case 'video':
			this._mainDisplayTag = sr.appendChild(<video class="videoView" controls="controls" hidden=""/>) as HTMLVideoElement;
			break;
			// TODO : poster
		case 'audio':
			this._mainDisplayTag = sr.appendChild(<audio class="audioView" controls="controls" hidden=""/>) as HTMLAudioElement;
			break;
		default :
			// iframe
			this._mainDisplayTag = sr.appendChild(<iframe class="htmlView" hidden="" sandbox="allow-scripts allow-same-origin allow-forms"/>) as HTMLIFrameElement;
			break;
		}
		this._mainDisplayTag.onerror = function (this: HTMLIFrameElement, ev: Event) {
			DOMSH.findHost<RemoteView>(this).showMainDisplayTag(false);
			DOMSH.findHost<RemoteView>(this)._pending.setCustomMsg("Non disponible", "error");
		};
		this._pending = sr.appendChild(<c-msg/>) as MsgLabel;
	}

	protected _refresh() {
		this._tryIndex = -1;
		this.showMainDisplayTag(false);
		this._pending.setStandardMsg("loading");
		const error: { msg?: string | undefined } = {};
		RemoteView.resolveRemote(this.wsp, this.config, this.fields, this.location, error).then(async (url) => {
			if (error.msg) {
				this.showMainDisplayTag(false);
				this._pending.setCustomMsg(error.msg, "info");
			} else if (url) {
				try {
					this.setUrl(url);
				} catch (e) {
					if (DEBUG) console.log(e);
					this.showMainDisplayTag(false);
					this._pending.setCustomMsg("Visualisation non disponible", "info");
				}
			} else {
				this.showMainDisplayTag(false);
				this._pending.setCustomMsg("Visualisation non disponible", "info");
			}
		});
	}

	static async resolveRemote(wsp: Wsp, viewers: ORemoteviewer, srcFields: JSrcFields, location?: string, error?: { msg?: string | undefined }): Promise<string | null> {
		if (!srcFields || srcFields.srcSt < 0) {
			if (error) error.msg = "Source non spécifiée";
			return;
		}
		let tryIndex = -1;
		let locationParam = "";
		if (location) locationParam += "&location=" + encodeURIComponent(location);
		do {
			const viewer = viewers.viewers[++tryIndex];
			if (!viewer) {
				if (error) error.msg = "Visualisation non disponible";
				return;
			} else if (viewer.type === "cidPreviewProcess") {
				console.log("TODO cidPreviewProcess");
			} else if (viewer.type === "scDepotView") {
				try {
					const url = await WSP.fetchStreamText(wsp, null, SRC.srcRef(srcFields), "transform=facet&facet=remoteContent&outType=LOCATION&scDepotView=" + encodeURIComponent(viewer.code) + locationParam);
					if (await RemoteView.isValidUrl(url)) return url;
				} catch (e) {
					if (DEBUG) console.log(e);
				}
			} else if (viewer.type === "locationAsSrc") {
				try {
					let transform = "transform=facet&facet=remoteContent&outType=LOCATION" + locationParam;
					viewer.queryStringParams?.forEach((param) => {
						transform += "&queryString=";
						if (param.keySvc)
							transform += param.keySvc + ":";
						transform += CDM.stringify(param.value);
					});
					const url = await WSP.fetchStreamText(wsp, null, SRC.srcRef(srcFields), transform);
					if (await RemoteView.isValidUrl(url, viewer.queryStringParams && viewer.queryStringParams.length ? false : true)) return url;
				} catch (e) {
					if (DEBUG) console.log(e);
				}
			}
		} while (true);
	}

	protected showMainDisplayTag(show: boolean) {
		DOM.setHidden(this._pending, show);
		DOM.setHidden(this._mainDisplayTag, !show);
	}

	/**
	 *
	 * @param noRemoteCheck pour les resources publiques, permet l'affichage direct en iframe, sans les restrictions CORS.
	 */
	static async isValidUrl(url: string, noRemoteCheck?: boolean): Promise<boolean> {
		if (url && /https?\:\/\/.*/.test(url)) {
			if (noRemoteCheck) return true;
			const resp = await fetch(url, {method: "HEAD", credentials: "include"});//mode:"no-cors", redirect:"follow"
			if (resp.ok) return true;
		}
		return false;
	}

	protected setUrl(url: string) {
		this.showMainDisplayTag(true);
		this._mainDisplayTag.src = url;
	}
}

REG.reg.registerSkin('wsp-remote-view', 1, /* language=CSS */ `
	:host {
		flex: 1;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		text-align: center;
		vertical-align: middle;
		overflow: auto;
		display: flex;
	}


	:host > .imgView, :host > .videoView, :host > .audioView, :host > .iframeView, :host > .dynGenView {
		max-height: 100%;
		object-fit: scale-down;
		max-width: 100%;
	}

	:host(.itemPreview) > .imgView, :host(.itemPreview) > .videoView, :host(.itemPreview) > .iframeView, :host(.itemPreview) > .dynGenView {
		max-height: 10em;
	}

	:host > .audioView {
		max-height: 2em;
	}

	:host > .iframeView, :host > .dynGenView {
		border: 1px solid var(--border-color);
		width: 100%;
	}

	[hidden] {
		display: none;
	}

	iframe {
		width: 100%;
		height: 100%;
		border: none;
	}

	c-msg {
		margin: .8em;
		font-style: italic;
	}
`);

customElements.define('wsp-remote-view', RemoteView);

const DEBUG = false;
